import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Alert,
    Divider,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../store';
import { hidePaymentErrorModal, clearPaymentError } from '../store/slices/uiSlice';

export const PaymentErrorModal: React.FC = () => {
    const dispatch = useDispatch();
    const { paymentError, showPaymentErrorModal } = useSelector(
        (state: RootState) => state.ui
    );

    const handleClose = () => {
        dispatch(hidePaymentErrorModal());
    };

    const handleRetry = () => {
        // Close the modal and trigger payment retry
        dispatch(clearPaymentError());
        // TODO: Add retry logic here (redirect to payment or trigger payment flow)
        window.location.reload(); // Simple reload for now
    };

    const handleUseAnotherCard = () => {
        // Close modal and redirect to payment selection
        dispatch(clearPaymentError());
        // TODO: Add logic to return to payment method selection
        window.location.reload(); // Simple reload for now
    };

    if (!paymentError) return null;

    const { error, userName, tableNumber } = paymentError;

    const getSeverityColor = (severity: string) => {
        switch (severity) {
            case 'error':
                return 'error';
            case 'warning':
                return 'warning';
            case 'info':
                return 'info';
            default:
                return 'warning';
        }
    };

    const getActionButtons = () => {
        switch (error.category) {
            case 'retry':
                return (
                    <>
                        <Button onClick={handleClose} color="inherit">
                            Cerrar
                        </Button>
                        <Button
                            onClick={handleRetry}
                            variant="contained"
                            color="primary"
                            autoFocus
                        >
                            {error.icon} Intentar Nuevamente
                        </Button>
                    </>
                );

            case 'no_retry':
                return (
                    <>
                        <Button onClick={handleClose} color="inherit">
                            Cerrar
                        </Button>
                        <Button
                            onClick={handleUseAnotherCard}
                            variant="contained"
                            color="primary"
                            autoFocus
                        >
                            Usar Otra Tarjeta
                        </Button>
                    </>
                );

            case 'contact':
                return (
                    <>
                        <Button onClick={handleClose} color="inherit">
                            Cerrar
                        </Button>
                        <Button
                            onClick={handleUseAnotherCard}
                            variant="contained"
                            color="primary"
                            autoFocus
                        >
                            Usar Otra Tarjeta
                        </Button>
                    </>
                );

            default:
                return (
                    <Button onClick={handleClose} variant="contained" color="primary">
                        Cerrar
                    </Button>
                );
        }
    };

    return (
        <Dialog
            open={showPaymentErrorModal}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            aria-labelledby="payment-error-dialog-title"
        >
            <DialogTitle id="payment-error-dialog-title">
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h6" component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: '8px' }}>{error.icon}</span> Error en el Pago
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent>
                <Alert
                    severity={getSeverityColor(error.severity) as any}
                    icon={false}
                    sx={{ mb: 2, display: 'flex', alignItems: 'center' }}
                >
                    {error.userMessage}
                </Alert>

                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Usuario:</strong> {userName}
                    </Typography>
                    {tableNumber && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            <strong>Mesa:</strong> {tableNumber}
                        </Typography>
                    )}
                    {error.bank && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            <strong>Banco:</strong> {error.bank}
                        </Typography>
                    )}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box>
                    <Typography variant="body2" color="text.secondary">
                        <strong>Recomendación:</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {error.category === 'retry' && 'Este es un error temporal. Puedes intentar nuevamente en unos minutos.'}
                        {error.category === 'no_retry' && 'Te recomendamos usar una tarjeta diferente o contactar a tu banco.'}
                        {error.category === 'contact' && 'Es necesario contactar al soporte técnico o a tu banco para resolver este problema.'}
                    </Typography>
                </Box>

                {import.meta.env.DEV && (
                    <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            <strong>Debug:</strong> Código {error.code} | {error.originalMessage} | Origen: {paymentError.source}
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                {getActionButtons()}
            </DialogActions>
        </Dialog>
    );
}; 